import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileUpload } from '@/components/ui/file-upload';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { User, Building2, Loader2, Copy, Bell, LogOut, ArrowLeft, CreditCard, Shield, Users } from 'lucide-react';
import { BillingSection } from '@/components/team/BillingSection';

import { TeamMembersSection } from '@/components/team/TeamMembersSection';
import { useAuth } from '@/components/auth/AuthProvider';
import { useCurrentTeam } from '@/hooks/useTeams';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
type SettingsSection = 'profile' | 'notifications' | 'team' | 'members' | 'billing';
type AccountTab = 'profile' | 'notifications';
type TeamTab = 'company-profile' | 'members';
interface PartnerProfile {
  first_name?: string;
  last_name?: string;
  phone?: string;
  bio?: string;
  company_name?: string;
  company_website?: string;
  industry?: string;
  booking_page_url?: string;
  company_description?: string;
  display_name?: string;
  profile_picture_url?: string;
  company_logo_url?: string;
  partner_slug?: string;
}
interface TeamData {
  name?: string;
  company_name?: string;
  company_logo_url?: string;
  company_description?: string;
  booking_page_url?: string;
}
export const SettingsModal = ({
  open,
  onOpenChange
}: SettingsModalProps) => {
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');
  const [profileData, setProfileData] = useState<PartnerProfile>({});
  const [teamData, setTeamData] = useState<TeamData>({});
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    user,
    signOut
  } = useAuth();
  const {
    currentTeamId
  } = useCurrentTeam();
  const {
    toast
  } = useToast();
  useEffect(() => {
    if (open && user) {
      loadProfileData();
      loadTeamData();
    }
  }, [open, user, currentTeamId]);

  const loadProfileData = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.from('user_profiles').select('*').eq('user_id', user.id).maybeSingle();
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      setProfileData(data || {});
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const loadTeamData = async () => {
    if (!user || !currentTeamId) return;
    try {
      const {
        data,
        error
      } = await supabase.from('teams').select('*').eq('id', currentTeamId).maybeSingle();
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      setTeamData(data || {});
    } catch (error) {
      console.error('Error loading team data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team data',
        variant: 'destructive'
      });
    }
  };
  const updateProfileData = (field: keyof PartnerProfile, value: string | null) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const updateTeamData = (field: keyof TeamData, value: string | null) => {
    setTeamData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const uploadFile = async (file: File, bucket: string, folder: string): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${folder}/${Date.now()}.${fileExt}`;
    const {
      error: uploadError
    } = await supabase.storage.from(bucket).upload(fileName, file, {
      upsert: true
    });
    if (uploadError) throw uploadError;
    const {
      data
    } = supabase.storage.from(bucket).getPublicUrl(fileName);
    return data.publicUrl;
  };
  const handleProfilePictureUpload = async (file: File) => {
    try {
      const url = await uploadFile(file, 'profile-pictures', 'avatar');
      updateProfileData('profile_picture_url', url);
      const {
        error
      } = await supabase.from('user_profiles').upsert({
        user_id: user!.id,
        profile_picture_url: url
      }, {
        onConflict: 'user_id'
      });
      if (error) throw error;
      toast({
        title: 'Success',
        description: 'Profile picture updated successfully'
      });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload profile picture',
        variant: 'destructive'
      });
    }
  };
  const handleCompanyLogoUpload = async (file: File) => {
    if (!currentTeamId) return;
    try {
      const url = await uploadFile(file, 'company-logos', 'logo');
      updateTeamData('company_logo_url', url);
      const {
        error
      } = await supabase.from('teams').update({
        company_logo_url: url
      }).eq('id', currentTeamId);
      if (error) throw error;
      toast({
        title: 'Success',
        description: 'Company logo updated successfully'
      });
    } catch (error) {
      console.error('Error uploading company logo:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload company logo',
        variant: 'destructive'
      });
    }
  };
  const saveProfileData = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const {
        error
      } = await supabase.from('user_profiles').upsert({
        user_id: user.id,
        first_name: profileData.first_name || null,
        last_name: profileData.last_name || null,
        phone: profileData.phone || null,
        bio: profileData.bio || null,
        display_name: profileData.display_name || null
      }, {
        onConflict: 'user_id'
      });
      if (error) throw error;
      toast({
        title: 'Success',
        description: 'Profile updated successfully'
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile data',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };
  const saveCompanyData = async () => {
    if (!user || !currentTeamId) return;
    setIsSaving(true);
    try {
      const {
        error
      } = await supabase.from('teams').update({
        name: teamData.name || null,
        company_name: teamData.company_name || null,
        company_description: teamData.company_description || null,
        booking_page_url: teamData.booking_page_url || null
      }).eq('id', currentTeamId);
      if (error) throw error;
      toast({
        title: 'Success',
        description: 'Company information updated successfully'
      });
    } catch (error) {
      console.error('Error saving company data:', error);
      toast({
        title: 'Error',
        description: 'Failed to save company information',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleLogOut = async () => {
    try {
      await signOut();
      onOpenChange(false);
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to log out. Please try again.',
        variant: 'destructive'
      });
    }
  };

  // Get current section details
  const getSectionDetails = (section: SettingsSection) => {
    switch (section) {
      case 'profile':
        return {
          icon: User,
          name: 'Profile'
        };
      case 'notifications':
        return {
          icon: Bell,
          name: 'Notifications'
        };
      case 'team':
        return {
          icon: Building2,
          name: 'General'
        };
      case 'members':
        return {
          icon: Users,
          name: 'Members'
        };
      case 'billing':
        return {
          icon: CreditCard,
          name: 'Billing'
        };
      default:
        return {
          icon: User,
          name: 'Profile'
        };
    }
  };
  const currentSection = getSectionDetails(activeSection);
  const CurrentIcon = currentSection.icon;
  if (isLoading) {
    return <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none p-0 m-0 border-0 rounded-none">
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>;
  }
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-4 bg-background border border-[#f0f0f0] rounded-lg overflow-hidden backdrop-blur-sm p-0">
        <div className="flex h-full">
            {/* Sidebar */}
            <div className="w-60 border-r p-4 flex flex-col overflow-hidden" style={{
            backgroundColor: '#fafafa'
          }}>
            {/* Settings Header with Back Button */}
            <div className="flex items-center gap-2 mb-6">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                Settings
              </h2>
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto">
              {/* Account Settings */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Account Settings</h3>
                <div className="space-y-1">
                  <button onClick={() => setActiveSection('profile')} className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors border ${activeSection === 'profile' ? 'bg-[#FDFDFD] border-[#F0F0F0] shadow-sm text-[#292929]' : 'hover:bg-[#FAFAFA] hover:font-medium hover:text-[#505050] font-medium text-[#1A1A1A] border-transparent'}`}>
                    <User className="h-4 w-4" />
                    Profile
                  </button>
                  <button onClick={() => setActiveSection('notifications')} className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors border ${activeSection === 'notifications' ? 'bg-[#FDFDFD] border-[#F0F0F0] shadow-sm text-[#292929]' : 'hover:bg-[#FAFAFA] hover:font-medium hover:text-[#505050] font-medium text-[#1A1A1A] border-transparent'}`}>
                    <Bell className="h-4 w-4" />
                    Notifications
                  </button>
                </div>
              </div>

              {/* Team Settings */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Team Settings</h3>
                <div className="space-y-1">
                  <button onClick={() => setActiveSection('team')} className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors border ${activeSection === 'team' ? 'bg-[#FDFDFD] border-[#F0F0F0] shadow-sm text-[#292929]' : 'hover:bg-[#FAFAFA] hover:font-medium hover:text-[#505050] font-medium text-[#1A1A1A] border-transparent'}`}>
                    <Building2 className="h-4 w-4" />
                    General
                  </button>
                  <button onClick={() => setActiveSection('members')} className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors border ${activeSection === 'members' ? 'bg-[#FDFDFD] border-[#F0F0F0] shadow-sm text-[#292929]' : 'hover:bg-[#FAFAFA] hover:font-medium hover:text-[#505050] font-medium text-[#1A1A1A] border-transparent'}`}>
                    <Users className="h-4 w-4" />
                    Members
                  </button>
                  <button onClick={() => setActiveSection('billing')} className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors border ${activeSection === 'billing' ? 'bg-[#FDFDFD] border-[#F0F0F0] shadow-sm text-[#292929]' : 'hover:bg-[#FAFAFA] hover:font-medium hover:text-[#505050] font-medium text-[#1A1A1A] border-transparent'}`}>
                    <CreditCard className="h-4 w-4" />
                    Billing
                  </button>
                </div>
              </div>

            </div>

            <div className="border-t pt-4 mt-4">
              {/* Log Out Button */}
              <Button onClick={handleLogOut} variant="ghost" className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10">
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </Button>
            </div>
          </div>

          {/* Main Content - Now Scrollable */}
          <div className="flex-1 overflow-hidden">
            {/* Header Bar */}
            <div className="border-b bg-background px-6 py-4">
              <div className="flex items-center gap-2">
                <CurrentIcon className="h-5 w-5" />
                <h1 className="text-lg font-semibold">{currentSection.name}</h1>
              </div>
            </div>
            
            <div className="h-full overflow-y-auto">
              <div className="max-w-3xl mx-auto p-6">
                {activeSection === 'profile' && <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-semibold mb-2">Profile</h2>
                    <p className="text-muted-foreground">Manage your personal details and preferences.</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="mb-2">
                      <Label className="mb-2">Profile Picture</Label>
                      <FileUpload value={profileData.profile_picture_url} onChange={url => updateProfileData('profile_picture_url', url)} onFileSelect={handleProfilePictureUpload} type="profile" />
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor="firstName">First Name</Label>
                        <Input id="firstName" placeholder="Enter your first name" value={profileData.first_name || ''} onChange={e => updateProfileData('first_name', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input id="lastName" placeholder="Enter your last name" value={profileData.last_name || ''} onChange={e => updateProfileData('last_name', e.target.value)} />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input id="email" type="email" value={user?.email || ''} disabled className="bg-muted" />
                      <p className="text-xs text-muted-foreground mt-1">
                        Email cannot be changed from here
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" type="tel" placeholder="Enter your phone number" value={profileData.phone || ''} onChange={e => updateProfileData('phone', e.target.value)} />
                    </div>
                    
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea id="bio" placeholder="Tell us about yourself..." rows={3} value={profileData.bio || ''} onChange={e => updateProfileData('bio', e.target.value)} />
                    </div>
                    
                    <Button onClick={saveProfileData} disabled={isSaving}>
                      {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                </div>}

                {activeSection === 'notifications' && <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-semibold mb-2">Notifications</h2>
                    <p className="text-muted-foreground">Configure how you receive notifications.</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-center py-12 text-muted-foreground">
                      <div className="text-center">
                        <p>Notification settings coming soon</p>
                      </div>
                    </div>
                  </div>
                </div>}

                 {activeSection === 'team' && <div className="space-y-6">
                   <div>
                     <h2 className="text-2xl font-semibold mb-2">General</h2>
                     <p className="text-muted-foreground">Manage your company information and branding.</p>
                   </div>
                     
                     <div className="space-y-4">
                       <div>
                         <Label htmlFor="companyLogo">Company Logo</Label>
                         <FileUpload
                           type="logo"
                           value={teamData.company_logo_url || ''}
                           onChange={(url) => updateTeamData('company_logo_url', url)}
                           onFileSelect={handleCompanyLogoUpload}
                           disabled={isSaving}
                         />
                       </div>
                       
                       <div>
                         <Label htmlFor="teamName">Team Name</Label>
                         <Input id="teamName" placeholder="Enter team name" value={teamData.name || ''} onChange={e => updateTeamData('name', e.target.value)} />
                       </div>
                       
                       <div>
                         <Label htmlFor="companyName">Company Name</Label>
                         <Input id="companyName" placeholder="Enter your company name" value={teamData.company_name || ''} onChange={e => updateTeamData('company_name', e.target.value)} />
                       </div>
                       
                       <div>
                         <Label htmlFor="bookingUrl">Booking Page URL</Label>
                         <Input id="bookingUrl" type="url" placeholder="https://calendly.com/yourname or your booking link" value={teamData.booking_page_url || ''} onChange={e => updateTeamData('booking_page_url', e.target.value)} />
                         <p className="text-xs text-muted-foreground mt-1">
                           This link will be shown to clients to schedule calls with you
                         </p>
                       </div>
                       
                       <div>
                         <Label htmlFor="companyDescription">Company Description</Label>
                         <Textarea id="companyDescription" placeholder="Describe what your company does..." rows={4} value={teamData.company_description || ''} onChange={e => updateTeamData('company_description', e.target.value)} />
                       </div>
                      
                       <Button onClick={saveCompanyData} disabled={isSaving}>
                         {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                         Save Changes
                       </Button>
                      </div>
                   </div>}

                 {activeSection === 'members' && <div className="space-y-6">
                   <div>
                     <h2 className="text-2xl font-semibold mb-2">Team Members</h2>
                     <p className="text-muted-foreground">Manage your team members and send invitations.</p>
                   </div>
                   
                   <TeamMembersSection />
                 </div>}

                 {activeSection === 'billing' && currentTeamId && <div className="space-y-6">
                   <div>
                     <h2 className="text-2xl font-semibold mb-2">Billing</h2>
                     <p className="text-muted-foreground">Manage your team's subscription and billing.</p>
                   </div>
                   
                   <BillingSection teamId={currentTeamId} />
                  </div>}

              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};